import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useIsFocused } from '@react-navigation/native';
import { AppView, Button, ButtonGroup, CardView, rem, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import Indicator from 'design-system/Indicator';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import RowSubscription from '../components/row/RowSubscription';
import { makeSelectDuplicateGroupBySubscriptionId, selectDuplicateGroups, } from '../selectors';
import { duplicateReviewStrings } from '../utils/duplicateReview';
import { getSubscriptionName } from '../utils/subscriptionName';
import { isWeb } from '../../../constants';
const strings = {
    pageTitle: 'Possible duplicate',
    title: (name, count) => count > 2
        ? `Paying for ${name} more than once?`
        : `Paying for ${name} twice?`,
    subTitle: (name, count) => `We found ${count > 2 ? count : 'two'} separate recurring charges for ${name}. Let’s make sure your subscriptions are set up correctly`,
    keep: (count) => count > 2 ? 'Keep all subscriptions' : 'Keep both subscriptions',
    cancel: 'Cancel duplicate',
};
const DuplicateReviewScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const step = route.params?.step ?? 0;
    const downloaded = useAppSelector((store) => store.subscriptions.subscriptions.downloaded);
    // A reloaded review URL on web has no ids: review every group, as from
    // the list, once the list has loaded. Taken once, so resolving a group
    // doesn't renumber the rest.
    const allGroups = useAppSelector(selectDuplicateGroups);
    const reloadedIds = useRef(undefined);
    if (!route.params?.subscriptionIds && downloaded && !reloadedIds.current) {
        reloadedIds.current = allGroups.map(([first]) => first.id);
    }
    const subscriptionIds = route.params?.subscriptionIds ?? reloadedIds.current ?? [];
    const total = subscriptionIds.length;
    const selectGroup = useMemo(makeSelectDuplicateGroupBySubscriptionId, []);
    const group = useAppSelector((store) => selectGroup(store, subscriptionIds[step]));
    // A group resolved since the flow opened drops out of the store: move on
    // to the next one, or leave the flow after the last. Only while focused:
    // a path screen above this one resolves the group before its own success
    // screen hands back to here.
    const isFocused = useIsFocused();
    useEffect(() => {
        if (group || !downloaded || !isFocused)
            return;
        if (step + 1 < total) {
            navigation.replace('DuplicateReview', {
                subscriptionIds,
                step: step + 1,
            });
        }
        else if (navigation.canGoBack()) {
            navigation.goBack();
        }
        else {
            // A reloaded review has nothing to go back to.
            navigation.replace('Subscriptions');
        }
    }, [
        group,
        downloaded,
        isFocused,
        step,
        total,
        subscriptionIds,
        navigation,
    ]);
    const onKeep = useCallback(() => {
        navigation.navigate('DuplicateKeepReasons', { subscriptionIds, step });
    }, [navigation, subscriptionIds, step]);
    const onAlreadyCancelled = useCallback(() => {
        navigation.navigate('DuplicateCancelled', { subscriptionIds, step });
    }, [navigation, subscriptionIds, step]);
    const onCancel = useCallback(() => {
        navigation.navigate('DuplicateCancel', { subscriptionIds, step });
    }, [navigation, subscriptionIds, step]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT * 3 + rem(16), paddingHorizontalStyle);
    if (!group) {
        // A reloaded review waits for the list; otherwise the effect above
        // moves on.
        return downloaded ? null : (<AppView withFrame={false}>
          <View style={staticStyles.loading}>
            <Indicator dark/>
          </View>
        </AppView>);
    }
    const name = getSubscriptionName(group[0]);
    const count = group.length;
    return (<AppView withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={paddingBottom}>
          <StatusBar barStyle="dark-content" animated/>
          <ScreenInfoHeader preTitle={duplicateReviewStrings.progress(step, total)} title={strings.title(name, count)} subTitle={strings.subTitle(name, count)}/>
          <CardView noMarginTop webPadding>
            {group.map((subscription) => (<RowSubscription key={subscription.id} data={subscription} hideDuplicateLabel/>))}
          </CardView>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button title={strings.keep(count)} brandBorder onPress={onKeep} testID="duplicate-review-keep"/>
          <Button title={duplicateReviewStrings.alreadyCancelled(count)} brandReversed onPress={onAlreadyCancelled} testID="duplicate-review-already-cancelled"/>
          <Button title={strings.cancel} brand onPress={onCancel} testID="duplicate-review-cancel"/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    title: '',
    pageTitle: strings.pageTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const staticStyles = StyleSheet.create({
    loading: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
export default DuplicateReviewScreen;
